var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import useSWR from "swr";
import axios from "axios";
import { PUBLIC_SWR_KEYS } from "../Utilities/swrKeys";
import { useLogging } from "../../Shared/hooks/useLogging/useLogging";
import { LogLevel } from "../../Shared/hooks/useLogging/Logging-types";
import { useCompanyAddressQuery } from "./useCompanyAddressQuery";
import { useEnvironment } from "../../Shared/components/EnvironmentProvider/EnvironmentProvider";
/**
 * Fetches geocoded coordinates for the company address using MapBox Geocoding API.
 *
 * ⚠️ INTENTIONALLY NOT HYDRATED
 *
 * Rationale for on-demand fetching (not hydrated):
 * - Cost Optimization: MapBox charges per API request. Hydrating on every login wastes money
 *   for users who never open address modals (majority of sessions).
 * - Performance: External API calls add latency to critical hydration. Geo-location is only
 *   needed for address modal map rendering, not core app functionality.
 * - Rate Limiting: Reduces unnecessary API calls that could hit MapBox rate limits under high traffic.
 * - Reliability: Keeps critical hydration fast and reliable by avoiding external dependencies.
 * - Data Staleness: Company address changes extremely rarely. No benefit from revalidation on every login.
 *
 * Trade-off: Small loading delay (~200-500ms) on first address modal open. Subsequent opens are instant (cached).
 *
 * Future optimization: Move geocoding to backend with server-side caching to eliminate runtime API calls.
 *
 * @returns Geocoded coordinates and place data from MapBox, or undefined if not yet fetched
 */
export const useCompanyGeoLocationQuery = () => {
    const { datadogLog } = useLogging();
    const { data: companyAddressData } = useCompanyAddressQuery();
    const { mapBoxApiKey } = useEnvironment();
    const companyAddress = companyAddressData === null || companyAddressData === void 0 ? void 0 : companyAddressData.companyAddress;
    // Only fetch if we have all required data
    const shouldFetch = !!companyAddress && !!mapBoxApiKey;
    const { data, error, isLoading } = useSWR(shouldFetch ? PUBLIC_SWR_KEYS.COMPANY_GEOLOCATION : null, () => __awaiter(void 0, void 0, void 0, function* () {
        if (!companyAddress || !mapBoxApiKey) {
            throw new Error("Missing required data for geocoding");
        }
        try {
            const encodedAddress = encodeURIComponent(companyAddress);
            const response = yield axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json`, {
                params: {
                    proximity: "ip",
                    access_token: mapBoxApiKey,
                    autocomplete: true,
                },
            });
            if (!response.data.features || response.data.features.length === 0) {
                throw new Error("No geocoding results found for company address");
            }
            const primaryFeature = response.data.features[0];
            return {
                coordinates: primaryFeature.center,
                placeName: primaryFeature.place_name,
                features: response.data.features,
            };
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error("Failed to geocode company address");
            datadogLog("Failed to geocode company address", LogLevel.ERROR, undefined, error);
            throw error;
        }
    }), {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        dedupingInterval: 3600000,
        onError: (error) => {
            datadogLog("MapBox geocoding request failed", LogLevel.ERROR, undefined, error);
        },
    });
    return {
        data,
        error,
        isLoading,
        coordinates: data === null || data === void 0 ? void 0 : data.coordinates,
        placeName: data === null || data === void 0 ? void 0 : data.placeName,
    };
};
